import React, { useMemo, useState } from 'react'
import { ICSModal, TanStackDatagrid, TanStackDatagridWithExport } from '../../../../components'
import {
  TanStackDatagridColumn,
  TanStackDatagridPagination
} from '../../../../components/TanStackDataGrid/TanStackDatagrid'
import RenderActions from '../ResultsCellRender/RenderActions'
import ICSErrorCard from '../../../../components/ICSErrorCard/ICSErrorCard'
import { ResultEntity } from '../../../../types/results'
import { getFileSize } from '../../../../utils/helpers'
import { ModalBody } from 'reactstrap'
import { DisplayErrorState } from '../../../../services/lib/types'
import './ResultFilesModal.css'

type ResultFilesModalError = Partial<DisplayErrorState>

export type ResultFilesDataSourceItem = {
  name: string
  size: string
  dataSpy: string
  resultFileId: string
}

export type ResultFilesDataSource = ResultFilesDataSourceItem[]

type ResultFilesModalProps = {
  selectedResult: ResultEntity
  resultsError?: ResultFilesModalError
  toggleModal: () => void
  show: boolean
}

export const resultFilesDataSource = (data: ResultEntity): ResultFilesDataSource => {
  if (!data?.files?.length) {
    return []
  }

  return data.files.map((file) => ({
    name: file.filename,
    size: getFileSize(file.size),
    dataSpy: `../dataspy/?ipaResultFileId=${file.id}&ipaResultId=${data.id}`,
    resultFileId: file.id
  }))
}

const ResultFilesModal: React.FC<ResultFilesModalProps> = ({
  selectedResult,
  resultsError,
  toggleModal,
  show
}) => {
  const [pagination, setPagination] = useState<TanStackDatagridPagination>({
    pageIndex: 0,
    pageSize: 10
  })
  const data = useMemo(
    () => resultFilesDataSource(selectedResult),
    [selectedResult]
  )

  const columns = useMemo<TanStackDatagridColumn<ResultFilesDataSourceItem>[]>(
    () => [
      {
        id: 'name',
        header: 'Name',
        accessor: (row) => row.name,
        sortable: true,
        filterable: true,
        resizable: true,
        hideable: true
      },
      {
        id: 'size',
        header: 'Size',
        accessor: (row) => row.size,
        sortable: true,
        filterable: true,
        resizable: true,
        hideable: true
      },
      {
        id: 'actions',
        header: 'Actions',
        accessor: () => 'Actions',
        cell: (row) => <RenderActions row={row} />,
        sortable: false,
        filterable: false,
        resizable: false,
        hideable: false
      }
    ],
    []
  )

  return (
    <ICSModal
      visible={show}
      onHiding={toggleModal}
      dragEnabled={false}
      closeOnOutsideClick={true}
      title={'Result Files'}
      defaultWidth={'75vw'}
      defaultHeight={'80vh'}
      resizeEnabled={true}
      id="ResultFilesModal"
    >
      <ModalBody className="result-files-modal__body">
        {resultsError?.code === 'error' && show ? (
          <div>
            <ICSErrorCard
              code={resultsError.code}
              message={resultsError.message || 'Request Error'}
              fields={resultsError.fields || []}
              title={'Error Downloading Result File'}
            />
          </div>
        ) : (
          <div className="result-files-modal__grid">
            <TanStackDatagridWithExport
              data={data}
              columns={columns}
              getRowId={(row) => row.resultFileId}
              emptyMessage="No result files available."
              enablePagination
              pagination={pagination}
              onPaginationChange={setPagination}
            />
          </div>
        )}
      </ModalBody>
    </ICSModal>
  )
}

export default ResultFilesModal
